import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CCard,
  CCardBody,
  CRow,
  CCol,
  CButton,
  CSpinner,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCloudDownload, cilWarning, cilCalendar } from "@coreui/icons";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import i18n from "../../../../i18n";

// Регистрируем русскую локаль
registerLocale("ru", ru);

// Функция форматирования длительности (Дни, Часы, Минуты)
const formatGapDuration = (ms) => {
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / (24 * 60));
  const h = Math.floor((totalMin % (24 * 60)) / 60);
  const m = totalMin % 60;

  let res = [];
  if (d > 0) res.push(`${d} дн.`);
  if (h > 0) res.push(`${h} ч.`);
  if (m > 0 || res.length === 0) res.push(`${m} мин.`);
  return res.join(' ');
};

const DisconnectionsModal = ({ visible, onClose, devices, selectedDevices }) => {
  const [loading, setLoading] = useState(false);
  const [gapsData, setGapsData] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Даты фильтра (по умолчанию сегодня)
  const [startDate, setStartDate] = useState(dayjs().startOf("day").toDate());
  const [endDate, setEndDate] = useState(dayjs().endOf("day").toDate());

  // Состояние для хранения дат, в которые есть данные
  const [availableDates, setAvailableDates] = useState(null);

  // Авто-сброс и загрузка дат при открытии
  useEffect(() => {
    if (visible) {
      setGapsData([]);
      if (selectedDevices.length > 0) {
        const fetchAvailableDates = async () => {
          try {
            const token = localStorage.getItem("token");
            const promises = selectedDevices.map((id) =>
              axios.get(`/api/devices/${id}/available-dates/`, {
                headers: { Authorization: `Bearer ${token}` },
              })
            );
            const results = await Promise.all(promises);
            const dateSet = new Set();
            results.forEach((res) => {
              res.data.forEach((dStr) => dateSet.add(dStr));
            });
            const dates = Array.from(dateSet).map((d) => new Date(d));
            setAvailableDates(dates);
          } catch (e) {
            console.error("Ошибка загрузки доступных дат:", e);
            setAvailableDates(null);
          }
        };
        fetchAvailableDates();
      } else {
        setAvailableDates(null);
      }
    }
  }, [visible, selectedDevices]);

  // Фильтр для календаря (разрешает кликать только на дни с данными)
  const isDateAvailable = (date) => {
    if (availableDates === null) return true; 
    if (availableDates.length === 0) return false;
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    return availableDates.some(d => dayjs(d).format('YYYY-MM-DD') === dateStr);
  };

  // Основная логика: Запрос аналитики и поиск дыр во времени
  const fetchGaps = async () => {
    if (selectedDevices.length === 0) {
      toast.warn("toast.warn(i18n.t('disconnections_modal.select_device_first', 'Сначала выберите технику на главной странице'))");
      return;
    }

    setLoading(true);
    setGapsData([]);

    try {
      const token = localStorage.getItem("token");
      const params = {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      };

      // Делаем параллельные запросы к API по выбранным устройствам
      const promises = selectedDevices.map((id) => {
        const devObj = devices.find((d) => d.id === id);
        return axios
          .get("/api/analytics/", {
            headers: { Authorization: `Bearer ${token}` },
            params: { ...params, device_id: id },
          })
          .then((res) => ({ device: devObj, data: res.data }))
          .catch((err) => ({ device: devObj, data: null }));
      });

      const results = await Promise.all(promises);
      const allGaps = [];

      results.forEach((res) => {
        const sDate = startDate.getTime();
        const eDate = endDate.getTime();

        // 1. Сценарий: Данных вообще нет (Техника оффлайн весь период)
        if (!res.data || !res.data.timeline || res.data.timeline.length === 0) {
          const gapMs = eDate - sDate;
          allGaps.push({
            deviceName: res.device.name,
            disconnectTime: new Date(sDate),
            restoreTime: new Date(eDate),
            durationMs: gapMs,
            ongoing: true
          });
          return;
        }

        // 2. Сценарий: Ищем разрывы между рабочими интервалами
        let currentCursor = sDate;
        const sortedTimeline = [...res.data.timeline].sort(
          (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
        );

        sortedTimeline.forEach((seg) => {
          const segStart = new Date(seg.start).getTime();
          const segEnd = new Date(seg.end).getTime();

          // Если между курсором и началом сегмента есть дыра > 5 минут
          if (segStart > currentCursor) {
            const gapMs = segStart - currentCursor;
            if (gapMs > 5 * 60 * 1000) { 
              allGaps.push({
                deviceName: res.device.name,
                disconnectTime: new Date(currentCursor),
                restoreTime: new Date(segStart),
                durationMs: gapMs,
                ongoing: false
              });
            }
          }
          if (segEnd > currentCursor) {
            currentCursor = segEnd;
          }
        });

        // 3. Сценарий: Разрыв от последнего лога до конца периода
        if (currentCursor < eDate) {
          const gapMs = eDate - currentCursor;
          if (gapMs > 5 * 60 * 1000) {
            allGaps.push({
              deviceName: res.device.name,
              disconnectTime: new Date(currentCursor),
              restoreTime: new Date(eDate),
              durationMs: gapMs,
              ongoing: true
            });
          }
        }
      });

      // Сортируем от самых долгих простоев к мелким
      allGaps.sort((a, b) => b.durationMs - a.durationMs);
      setGapsData(allGaps);

    } catch (e) {
      toast.error("toast.error(i18n.t('disconnections_modal.error_fetching_disconnections', 'Ошибка при поиске отключений'))");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Экспорт в Excel
  const exportExcel = async () => {
    setExporting(true);
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Журнал отключений");

      // Настройка ширины колонок
      ws.columns = [
        { header: "Техника", key: "device", width: 35 },
        { header: "Дата и время пропадания связи", key: "disconnect", width: 30 },
        { header: "Дата и время восстановления", key: "restore", width: 30 },
        { header: "Длительность разрыва", key: "duration", width: 25 },
      ];

      // Стилизация шапки
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE55353" } };
      headerRow.alignment = { vertical: "middle", horizontal: "center" };
      headerRow.height = 25;

      // Заполнение данными
      gapsData.forEach((gap) => {
        const row = ws.addRow({
          device: gap.deviceName,
          disconnect: dayjs(gap.disconnectTime).format("DD.MM.YYYY HH:mm"),
          restore: gap.ongoing ? "{i18n.t('disconnections_modal.no_connection_end_period', 'Нет связи (по конец периода)')}" : dayjs(gap.restoreTime).format("DD.MM.YYYY HH:mm"),
          duration: formatGapDuration(gap.durationMs)
        });

        // Стилизация ячеек
        row.getCell(1).font = { bold: true };
        row.getCell(4).font = { bold: true, color: { argb: "FFC00000" } };
        row.eachCell((cell, colNum) => {
          if (colNum > 1) cell.alignment = { horizontal: "center" };
          cell.border = { bottom: { style: "thin", color: { argb: "FFCCCCCC" } } };
        });
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `Отключения_Связи_${dayjs().format("YYYY-MM-DD")}.xlsx`);
      toast.success("toast.success(i18n.t('disconnections_modal.report_downloaded', 'Отчет успешно скачан!'))");
    } catch (e) {
      toast.error("toast.error(i18n.t('disconnections_modal.export_error', 'Ошибка экспорта'))");
    } finally {
      setExporting(false);
    }
  };

  return (
    <CModal visible={visible} onClose={onClose} size="xl" alignment="center">
      <CModalHeader className="bg-light">
        <CModalTitle className="d-flex align-items-center text-danger fw-bold">
          <CIcon icon={cilWarning} className="me-2" size="xl" />
          {i18n.t('disconnections_modal.connection_loss_log', 'Журнал пропадания связи (Отключения)')}
        </CModalTitle>
      </CModalHeader>

      <CModalBody className="bg-light p-3">
        <CCard className="mb-4 shadow-sm border-0">
          <CCardBody>
            <CRow className="align-items-end g-3">
              <CCol md={3}>
                <label className="fw-bold small text-muted text-uppercase mb-1">С даты:</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  className="form-control"
                  showTimeSelect
                  timeCaption="Время" 
                  dateFormat="dd.MM.yyyy HH:mm"
                  locale="ru"
                  filterDate={isDateAvailable}
                />
              </CCol>
              <CCol md={3}>
                <label className="fw-bold small text-muted text-uppercase mb-1">По дату:</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  className="form-control"
                  showTimeSelect
                  timeCaption="Время" 
                  dateFormat="dd.MM.yyyy HH:mm"
                  locale="ru"
                  filterDate={isDateAvailable}
                />
              </CCol>
              <CCol md={3}>
                <CButton color="danger" onClick={fetchGaps} disabled={loading} className="w-100 fw-bold text-white">
                  {loading ? <CSpinner size="sm" className="me-2" /> : "{i18n.t('disconnections_modal.find_disconnections', 'Найти отключения')}"}
                </CButton>
              </CCol>
              <CCol md={3}>
                <CButton color="success" variant="outline" onClick={exportExcel} disabled={exporting || gapsData.length === 0} className="w-100 fw-bold">
                  {exporting ? <CSpinner size="sm" /> : <><CIcon icon={cilCloudDownload} className="me-2" /> {i18n.t('disconnections_modal.export_to_excel', 'В Excel')}</>}
                </CButton>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CCard className="shadow-sm border-0">
          <div className="table-responsive" style={{ maxHeight: "500px" }}>
            <CTable align="middle" className="mb-0 border-0" small hover>
              <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <CTableRow>
                  <CTableHeaderCell className="ps-3">Техника</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Дата и время пропадания</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Восстановление связи</CTableHeaderCell>
                  <CTableHeaderCell className="text-center pe-3">Длительность разрыва</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody className="bg-white">
                {gapsData.length > 0 ? (
                  gapsData.map((gap, i) => (
                    <CTableRow key={i}>
                      <CTableDataCell className="fw-bold ps-3 text-dark">
                        {gap.deviceName}
                      </CTableDataCell>
                      <CTableDataCell className="text-center fw-semibold">
                        {dayjs(gap.disconnectTime).format("DD.MM.YY HH:mm")}
                      </CTableDataCell>
                      <CTableDataCell className="text-center fw-semibold">
                        {gap.ongoing ? (
                          <span className="text-muted fst-italic">Нет связи</span>
                        ) : (
                          dayjs(gap.restoreTime).format("DD.MM.YY HH:mm")
                        )}
                      </CTableDataCell>
                      <CTableDataCell className="text-center pe-3">
                        <CBadge color="danger" shape="rounded-pill" className="px-3 py-2 fs-6">
                          {formatGapDuration(gap.durationMs)}
                        </CBadge>
                      </CTableDataCell>
                    </CTableRow>
                  ))
                ) : (
                  <CTableRow>
                    <CTableDataCell colSpan={4} className="text-center p-5 text-muted">
                      {loading ? "{i18n.t('disconnections_modal.collecting_data', 'Идет сбор данных...')}" : "{i18n.t('disconnections_modal.press_find_disconnections', 'Нажмите «Найти отключения», чтобы сформировать отчет')}"}
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
      </CModalBody>
    </CModal>
  );
};

export default DisconnectionsModal;